import { useSelect } from '@refinedev/antd'
import {
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Card,
  Row,
  Col,
  Button,
  Space,
  Divider,
} from 'antd'
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons'
import { ImageGalleryInput } from './ImageGalleryInput'

const { TextArea } = Input

/** Shared form body for product create + edit. */
export function ProductFormFields() {
  const { selectProps: categorySelectProps } = useSelect({
    resource: 'categories',
    optionLabel: 'name',
    optionValue: 'id',
    pagination: { pageSize: 100, mode: 'server' },
  })

  return (
    <>
      {/* Basics */}
      <Card title="Osnovni podaci" style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              label="Naziv"
              name="name"
              rules={[{ required: true, message: 'Naziv je obavezan' }]}
            >
              <Input placeholder="npr. Omega-3 Premium" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              label="Slug (URL)"
              name="slug"
              rules={[
                { required: true, message: 'Slug je obavezan' },
                {
                  pattern: /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
                  message: 'Samo mala slova, brojevi i crtice',
                },
              ]}
            >
              <Input placeholder="omega-3-premium" />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item label="Kratak opis" name="shortDescription">
              <Input placeholder="Jedna rečenica o proizvodu (prikazuje se na kartici)" />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item label="Detaljan opis" name="description">
              <TextArea rows={4} placeholder="Puni opis proizvoda" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label="Kategorija" name="categoryId">
              <Select {...categorySelectProps} allowClear placeholder="Izaberite kategoriju" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={6}>
            <Form.Item label="Oznaka" name="badge">
              <Select allowClear placeholder="Nema">
                <Select.Option value="BESTSELLER">Bestseler</Select.Option>
                <Select.Option value="NEW">Novo</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={6}>
            <Form.Item label="Tip (interno)" name="type" extra="Za blood test/konsultaciju">
              <Select allowClear placeholder="Nema">
                <Select.Option value="BLOOD_TEST">Blood test</Select.Option>
                <Select.Option value="TREATMENT">Treatment</Select.Option>
                <Select.Option value="CONSULTATION">Consultation</Select.Option>
                <Select.Option value="SUBSCRIPTION">Subscription</Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
      </Card>

      {/* Pricing + subscription */}
      <Card title="Cena i pretplata" style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col xs={24} sm={8}>
            <Form.Item
              label="Cena"
              name="price"
              rules={[{ required: true, message: 'Cena je obavezna' }]}
            >
              <InputNumber style={{ width: '100%' }} min={0} step={0.01} placeholder="2490" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item label="Stara cena (precrtana)" name="compareAtPrice">
              <InputNumber style={{ width: '100%' }} min={0} step={0.01} placeholder="2990" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item label="Valuta" name="currency" initialValue="RSD">
              <Select>
                <Select.Option value="RSD">RSD</Select.Option>
                <Select.Option value="EUR">EUR</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item label="Pretplata dostupna" name="subscriptionEnabled" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item label="Cena pretplate / mesec" name="subscriptionPrice">
              <InputNumber style={{ width: '100%' }} min={0} step={0.01} placeholder="2115" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item label="Popust pretplate (%)" name="subscriptionDiscountPct">
              <InputNumber style={{ width: '100%' }} min={0} max={100} placeholder="15" />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      {/* Media */}
      <Card title="Slike" style={{ marginBottom: 16 }}>
        <Form.Item name="images" valuePropName="value" noStyle>
          <ImageGalleryInput />
        </Form.Item>
      </Card>

      {/* Inventory */}
      <Card title="Zalihe" style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item label="Status zaliha" name="stockStatus" initialValue="IN_STOCK">
              <Select>
                <Select.Option value="IN_STOCK">Na stanju</Select.Option>
                <Select.Option value="OUT_OF_STOCK">Nema na stanju</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label="Količina (opciono)" name="stock" extra="Ostavite prazno ako se ne prati">
              <InputNumber style={{ width: '100%' }} min={0} placeholder="npr. 120" />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      {/* Specifications */}
      <Card title="Sastav / specifikacija" style={{ marginBottom: 16 }}>
        <Form.List name="specs">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <Space key={key} align="baseline" style={{ display: 'flex', marginBottom: 8 }}>
                  <Form.Item
                    {...restField}
                    name={[name, 'name']}
                    rules={[{ required: true, message: 'Naziv sastojka' }]}
                    style={{ marginBottom: 0 }}
                  >
                    <Input placeholder="Sastojak (npr. EPA)" style={{ width: 220 }} />
                  </Form.Item>
                  <Form.Item {...restField} name={[name, 'amount']} style={{ marginBottom: 0 }}>
                    <Input placeholder="Količina (npr. 600 mg)" style={{ width: 160 }} />
                  </Form.Item>
                  <Form.Item {...restField} name={[name, 'nrv']} style={{ marginBottom: 0 }}>
                    <Input placeholder="%NRV" style={{ width: 100 }} />
                  </Form.Item>
                  <MinusCircleOutlined onClick={() => remove(name)} />
                </Space>
              ))}
              <Form.Item>
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  Dodaj red
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>

        <Divider />

        <Form.Item
          label="Sertifikati / oznake"
          name="certifications"
          extra="Unesite i pritisnite Enter (npr. Bez glutena, Bez laktoze)"
        >
          <Select mode="tags" placeholder="Bez glutena" tokenSeparators={[',']} />
        </Form.Item>
      </Card>

      {/* Usage & content */}
      <Card title="Upotreba i sadržaj" style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col xs={24} sm={8}>
            <Form.Item label="Doza" name={['usage', 'dose']}>
              <Input placeholder="2 kapsule dnevno" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item label="Način primene" name={['usage', 'administration']}>
              <Input placeholder="Uz obrok sa mastima" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item label="Napomena" name={['usage', 'notes']}>
              <Input placeholder="Doslednost je ključ" />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item label="Zdravstvene tvrdnje" name="healthClaims">
              <TextArea rows={2} placeholder="EPA i DHA doprinose normalnoj funkciji srca..." />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label="Čuvanje" name="storage">
              <Input placeholder="Na suvom i tamnom mestu do 25 °C" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label="Napomena / upozorenje" name="disclaimer">
              <Input placeholder="Nije zamena za raznovrsnu ishranu" />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      {/* Status */}
      <Card title="Status">
        <Form.Item
          label="Aktivan (vidljiv u prodavnici)"
          name="active"
          valuePropName="checked"
          initialValue={true}
        >
          <Switch />
        </Form.Item>
      </Card>
    </>
  )
}
